import { getProviderScopes, type Provider, type ScopeKey } from '@unipile/types';
import { useState, useCallback, useMemo } from 'react';
import { useHostedAuthSession } from './useHostedAuthSession';

type UseScopesConsentOptions = {
  provider: Provider;
  /**
   * Called when user cancels the consent.
   * If not provided, will redirect to the session's redirect_uri with an error.
   */
  onCancel?: () => void;
};

type UseScopesConsentReturn = {
  /**
   * The scopes that are relevant for this provider (filtered from session scopes)
   */
  scopes: ScopeKey[];
  /**
   * Whether there are scopes that require consent
   */
  hasScopes: boolean;
  /**
   * Whether the user has already consented
   */
  hasConsented: boolean;
  /**
   * Call this when user accepts the consent
   */
  acceptConsent: () => void;
  /**
   * Call this when user cancels/declines the consent
   */
  cancelConsent: () => void;
  /**
   * The brand name from the session (for display)
   */
  brandName?: string;
};

/**
 * Hook to manage scopes consent flow for a provider.
 *
 * This hook filters the session scopes to only those available for the given provider,
 * and provides state management for the consent flow.
 *
 * Usage:
 * ```tsx
 * const { scopes, hasScopes, hasConsented, acceptConsent, cancelConsent, brandName } = useScopesConsent({
 *   provider: 'linkedin',
 * });
 *
 * // In your flow, show ScopesConsentScreen when hasScopes && !hasConsented
 * if (hasScopes && !hasConsented) {
 *   return <ScopesConsentScreen scopes={scopes} brandName={brandName} onAccept={acceptConsent} onCancel={cancelConsent} />;
 * }
 * ```
 */
export function useScopesConsent({ provider, onCancel }: UseScopesConsentOptions): UseScopesConsentReturn {
  const { data: session } = useHostedAuthSession();

  // Filter scopes to only those available for this provider
  const providerAvailableScopes = useMemo(() => getProviderScopes(provider), [provider]);
  const scopes = useMemo(() => {
    if (!session?.scopes) return [];
    return session.scopes.filter((scope) => providerAvailableScopes.includes(scope));
  }, [session?.scopes, providerAvailableScopes]);

  const hasScopes = scopes.length > 0;

  // Start as consented if there are no scopes
  const [hasConsented, setHasConsented] = useState(!hasScopes);

  const acceptConsent = useCallback(() => {
    setHasConsented(true);
  }, []);

  const cancelConsent = useCallback(() => {
    if (onCancel) {
      onCancel();
      return;
    }

    // Default behavior: redirect to redirect_uri with error
    if (session?.redirect_uri) {
      const url = new URL(session.redirect_uri);
      url.searchParams.append('error_type', 'consent_denied');
      url.searchParams.append('error_title', 'Consent denied');
      url.searchParams.append('error_detail', 'User declined the consent request');
      window.location.href = url.toString();
    }
  }, [onCancel, session?.redirect_uri]);

  return {
    scopes,
    hasScopes,
    hasConsented,
    acceptConsent,
    cancelConsent,
    brandName: session?.brand_name,
  };
}
